// VsCallGuardianPage.js — SEO landing page targeting "Call Guardian alternative"
// and "Elevaite AI vs" queries. Public, no auth.
// Different wedge from vs-dialpad/aircall/ringcx: those are UCaaS-replacement
// stories; Call Guardian is IN OUR SAME CATEGORY (AI overlay on your existing
// PBX). The wedge is product-vs-bespoke, published pricing vs quote, 5 roles
// vs 1, evidence-based coaching vs prompt-tuned coaching.
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign, Layers, Zap, ShieldCheck, Package, Handshake } from 'lucide-react';

const COMPARISON_ROWS = [
  { label: 'Product model', cg: 'Bespoke consulting build per client', vf: 'Productised · self-serve tenant provisioning', highlight: true },
  { label: 'Per-user cost (AUD)', cg: 'Custom quote — not published', vf: 'A$49 – A$149 / user / mo (published)', highlight: true },
  { label: 'Setup fee', cg: 'Custom project quote', vf: 'A$3,500 – A$11,900 fixed' },
  { label: 'Free trial / free audit', cg: 'Book a Growth Consultation', vf: 'Free 3-call audit + 14-day free trial' },
  { label: 'Included minutes per user', cg: 'Not published', vf: '60 – 300 mins / user (grading + sentiment)' },
  { label: 'Overage rate', cg: 'Not published', vf: '$0.07/min transcription · $0.09/min sentiment' },
  { label: 'Roles graded', cg: 'Sales / customer service focus', vf: 'Sales · Support · Admin · Accounts · Management' },
  { label: 'Native phone system', cg: '3CX (bespoke integration)', vf: 'Yabbit UCaaS (native) + others via webhook' },
  { label: 'Coaching mechanic', cg: 'AI-generated emails · 66 prompt tweaks in first 24h', vf: 'KB-graded criteria + 3 evidence calls per rep' },
  { label: 'Reassessment tracking', cg: 'Not described', vf: 'Next 3 calls after coaching auto-tagged · before/after deltas captured' },
  { label: 'Governance escalation', cg: 'Flagged inside coaching workflow', vf: 'Independent of numeric score · streaks flag repeat offenders' },
  { label: 'Revivable Deal Queue', cg: 'Not offered', vf: 'Native — ranked by close-probability · DISC-matched · playbook opening line' },
  { label: 'White-label / reseller', cg: 'Not offered', vf: '50% reseller wholesale margin' },
  { label: 'Time to first coaching cycle', cg: 'Multi-week bespoke build phase', vf: '4 weeks · baseline scoring by week 3' },
  { label: 'Ongoing prompt tuning', cg: 'Chargeable consulting activity', vf: 'Baked in — you edit criteria in your KB, live in minutes' },
];

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-teal-500/15 text-teal-300' : 'bg-red-500/15 text-red-300'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const VsCallGuardianPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="vs-call-guardian-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/why-voicefabric" className="text-white/60 hover:text-white">Why VoiceFabric</Link>
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="vs-cg-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Call Guardian / Elevaite · Alternative for Australian teams</p>
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        Call Guardian is a great case study.<br /><span className="text-teal-400">VoiceFabric is the productised version.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        Elevaite&apos;s Call Guardian proves the category is real — you don&apos;t need to migrate off 3CX or Yabbit to unlock AI coaching.
        The difference is <strong className="text-white">what you buy</strong>: Call Guardian is a bespoke consulting engagement.
        Yabbit VoiceFabric is the shipping product, with published per-seat pricing, free 3-call audit, and 5-role coverage on day one.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/free-audit"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-cg-audit-cta"
        >
          Free 3-call audit
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-cg-pricing-cta"
        >
          See our pricing
        </Link>
      </div>
    </section>

    {/* Where we agree with Elevaite */}
    <section className="max-w-4xl mx-auto px-6 py-12">
      <div className="bg-teal-500/5 border border-teal-500/20 rounded-2xl p-8">
        <h2 className="text-white font-bold text-2xl mb-4">Where Elevaite is right</h2>
        <p className="text-white/70 leading-relaxed mb-4">
          We start here because it matters. Elevaite&apos;s April 2025 case study on Call Guardian is the clearest argument we&apos;ve read
          for the category Yabbit VoiceFabric operates in. Every diagnosis in that article is correct:
        </p>
        <ul className="space-y-2.5 text-white/80 text-sm">
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" /><span>Native Whisper transcription on 3CX or Yabbit is raw text, not intelligence.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" /><span>Most businesses review maybe 3% of their calls and lose the rest.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" /><span>CRM enrichment from call content is where the real revenue lives.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" /><span>Adoption is a psychology problem — staff need &quot;wins of the call&quot; before &quot;things to improve&quot;.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" /><span>Migrating to Dialpad or Aircall just to get AI is over-correction for a 20-seat business.</span></li>
        </ul>
      </div>
    </section>

    {/* At-a-glance callouts — what's different */}
    <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
      {[
        { icon: Package, title: 'Product, not project', body: 'Call Guardian is a bespoke build per client. VoiceFabric is one shipping product every tenant runs — same engine, same pricing, no consulting-hours dependency.' },
        { icon: DollarSign, title: 'Published pricing', body: 'A$49 / A$89 / A$149 per user per month with bundled minutes and published overage. No custom quote, no discovery call to see a number.' },
        { icon: Layers, title: 'Five roles, one tenant', body: "Call Guardian's case study is a car dealership sales team. VoiceFabric ships role KBs for sales, support, admin, accounts and management on day one." },
      ].map((c) => (
        <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-teal-400 mb-3" />
          <h3 className="text-white font-bold text-lg">{c.title}</h3>
          <p className="text-white/60 text-sm mt-1">{c.body}</p>
        </div>
      ))}
    </section>

    {/* Comparison table */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vs-cg-comparison">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Side-by-side · Call Guardian vs VoiceFabric</h2>
        <p className="text-white/50 text-sm mt-3">Call Guardian details sourced from Elevaite&apos;s April 2025 public case study. Feb 2026 snapshot.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/60 text-xs uppercase tracking-wider bg-white/5">
              <th className="px-4 py-3 text-left">Feature</th>
              <th className="px-4 py-3 text-left">Call Guardian (Elevaite)</th>
              <th className="px-4 py-3 text-left text-teal-300">Yabbit VoiceFabric</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COMPARISON_ROWS.map((r) => (
              <tr key={r.label} className={r.highlight ? 'bg-teal-500/5' : ''}>
                <td className="px-4 py-3 text-white/80 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-white/50"><Chip ok={false}>{r.cg}</Chip></td>
                <td className="px-4 py-3"><Chip ok>{r.vf}</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* The three real reasons */}
    <section className="max-w-4xl mx-auto px-6 py-16 space-y-10">
      <div className="text-center mb-6">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Three product mechanics Call Guardian doesn&apos;t attempt</h2>
      </div>

      {[
        {
          n: '01',
          title: 'Evidence-based coaching, not prompt-tuned coaching',
          body: 'Elevaite\'s article is candid: "66 prompt optimisations in the first 24 hours" to soften the tone of AI-generated coaching emails. That\'s the honest reality of running a coaching product on prompts alone — you\'re paying for that tuning forever. VoiceFabric ties every score to a specific criterion, a piece of transcript, an approved phrase from your KB, and three "evidence calls" — real examples from that rep\'s own history where they already did it right. Managers don\'t decide what to coach. The system tells them, with receipts.',
        },
        {
          n: '02',
          title: 'Reassessment tracking with before/after deltas',
          body: 'Call Guardian produces coaching insights. VoiceFabric auto-tags the next three calls after every coaching record so before/after score deltas are captured for you. When the CFO asks whether coaching moved the numbers, you show the chart. Not the emails.',
        },
        {
          n: '03',
          title: 'Governance escalation independent of the numeric score',
          body: 'A great-sounding call that misses a mandatory consent line still triggers a compliance review — even if the numeric score is 92. Governance streaks flag repeat offenders 30 days before they become a legal problem. Call Guardian bundles compliance inside coaching emails. VoiceFabric treats it as a first-class citizen with its own escalation path.',
        },
      ].map((r) => (
        <div key={r.n} className="flex gap-6 items-start">
          <div className="text-6xl font-black text-teal-500/40 leading-none">{r.n}</div>
          <div>
            <h3 className="text-white font-bold text-xl mb-2">{r.title}</h3>
            <p className="text-white/70 leading-relaxed">{r.body}</p>
          </div>
        </div>
      ))}
    </section>

    {/* Honest — when Call Guardian is the right pick */}
    <section className="max-w-3xl mx-auto px-6 py-12">
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
        <h3 className="text-amber-300 font-bold text-lg mb-3">Honest answer: when Call Guardian is the right choice</h3>
        <p className="text-white/70 leading-relaxed">
          If your business is <strong className="text-white">already on 3CX and committed to staying</strong>, you have
          <strong className="text-white"> 80+ phone-heavy seats</strong> and a healthy AI budget, you want a genuinely bespoke build
          (your workflows, your quirks, your custom prompts), and your GoHighLevel CRM integration depth matters more than platform-level features
          — Elevaite is a capable AI shop and Call Guardian is a legitimate choice.
        </p>
        <p className="text-white/70 leading-relaxed mt-3">
          If you&apos;re a phone-heavy <strong className="text-white">10 – 200 seat Australian SMB</strong> who wants per-seat pricing you can put in a budget forecast,
          five-role coverage on one tenant, and a free 3-call audit before committing a dollar — VoiceFabric is the shape of product that fits.
        </p>
      </div>
    </section>

    {/* Downloads */}
    <section className="max-w-4xl mx-auto px-6 py-12">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
        <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-2">Long-form article</p>
        <h3 className="text-white font-bold text-xl mb-3">Read the full response to Call Guardian</h3>
        <p className="text-white/60 text-sm mb-5 max-w-xl mx-auto">
          Our detailed response walks through every phase of Elevaite&apos;s Call Guardian case study — where we agree, where the productised model wins,
          and when Call Guardian is the right pick.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <a
            href="/downloads/Yabbit_Response_to_Call_Guardian.pdf"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
            data-testid="vs-cg-download-pdf"
          >
            Download PDF
          </a>
          <a
            href="/downloads/Yabbit_Response_to_Call_Guardian.docx"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
            data-testid="vs-cg-download-docx"
          >
            Download DOCX
          </a>
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Try the productised version.<br />No consultation call. No custom quote.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          Upload three of your own recordings. We&apos;ll grade them against a role-specific knowledge base and send back the exact coaching card each rep would see.
          Free — before you commit anything.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="vs-cg-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-4 gap-6 text-center">
        <div>
          <ShieldCheck className="w-7 h-7 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold text-sm">Australian data residency</h3>
        </div>
        <div>
          <Zap className="w-7 h-7 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold text-sm">Live in 4 weeks</h3>
        </div>
        <div>
          <Layers className="w-7 h-7 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold text-sm">5-role grading</h3>
        </div>
        <div>
          <Handshake className="w-7 h-7 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold text-sm">50% reseller margin</h3>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/why-voicefabric" className="hover:text-white/50">Why VoiceFabric</Link> · <Link to="/vs-dialpad" className="hover:text-white/50">vs Dialpad</Link> · <Link to="/vs-aircall" className="hover:text-white/50">vs Aircall</Link> · <Link to="/vs-ringcx" className="hover:text-white/50">vs RingCX</Link>
    </footer>
  </div>
);

export default VsCallGuardianPage;
